import { getSessionId } from "../utils/ServerRequestUtil";
import { ServiceError } from "./BaseTypes";
export type MethodType = "GET" | "PUT" | "POST" | "DELETE";
export type RequestOptions = {
	headers?: Record<string, string>;
	retry?: boolean;
	signal?: AbortSignal;
};
//Make this configurable, once you need it
const MAX_RETRY_COUNT = 10;

function waitFor(milliseconds: number): Promise<void> {
	return new Promise((resolve) => {
		setTimeout(resolve, milliseconds);
	});
}

export function backoff(base: number, min: number, max: number): (retryCount: number) => number {
	return (retryCount: number) => Math.min(Math.pow(base, retryCount - 1) * min, max) + 0.1 * Math.random() * min;
}
const retryableHttpErrorCodes = new Set([503, 504]);

async function retry(requester: () => Promise<any>): Promise<any> {
	const backoffStrategy = backoff(1.5, 500, 5000);
	let retryCount = 0;

	while (retryCount < MAX_RETRY_COUNT) {
		const response = await requester();

		if (!response.ok && retryableHttpErrorCodes.has(response.status)) {
			await waitFor(backoffStrategy(++retryCount));
			continue;
		}

		return response;
	}

	throw new ServiceError({
		code: "OC-896",
		message: "Too many retries.",
		type: "Retry",
	});
}

export async function doFetch(
	method: MethodType,
	url: string,
	body?: string | null,
	options?: RequestOptions,
): Promise<Response> {
	if (!options) {
		options = {};
	}

	const headers = {
		CallTreeId: getSessionId(),
		"Content-Type": "application/json; charset=UTF-8",
		...options.headers,
	};

	try {
		// We await, so we can catch cases where
		// fetch throws.
		return await fetch(url, {
			method,
			headers,
			body,
			signal: options.signal,
			credentials: "same-origin",
		});
	} catch (e) {
		throw new ServiceError({
			code: "OC-897",
			message: (e as any).message,
			type: "Fetch",
		});
	}
}
export class Request {
	method: MethodType;
	url: string;
	options: RequestOptions;

	constructor(method: MethodType, url: string) {
		this.method = method;
		this.url = url;
		this.options = {};
	}

	static get(url: string): Request {
		return new Request("GET", url).withTimestamp().retry(true);
	}

	static post(url: string): Request {
		return new Request("POST", url);
	}

	static put(url: string): Request {
		return new Request("PUT", url);
	}

	static delete(url: string): Request {
		return new Request("DELETE", url);
	}

	retry(value: boolean): Request {
		this.options.retry = value;
		return this;
	}

	timeout(timeoutMilliseconds: number): Request {
		if (!this.options.headers) {
			this.options.headers = {};
		}

		Object.assign(this.options.headers, {
			"x-timeout-ms": timeoutMilliseconds.toFixed().toString(),
		});
		return this;
	}

	/**
	 * IE has the habit to cache GET requests. This can be disabled by
	 * appending a random query string, such as a timestamp, to the
	 * request URL.
	 */
	withTimestamp(): Request {
		if (/\?/.test(this.url)) {
			this.url += "&__t=" + new Date().getTime();
		} else {
			this.url += "?__t=" + new Date().getTime();
		}

		return this;
	}

	withSignal(signal?: AbortSignal): Request {
		this.options.signal = signal;
		return this;
	}

	execute(body?: any): Promise<Response> {
		const payload: string | null | undefined = typeof body !== "string" ? JSON.stringify(body) : body;

		const sendRequest = () => doFetch(this.method, this.url, payload, this.options);

		if (this.options && this.options.retry) {
			return retry(sendRequest);
		}

		return sendRequest();
	}
}
